import { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";

import {
  PennButton,
  PennDropdown,
  PennField,
  PennInput,
  PennModal,
  PennText,
  PennTextarea,
} from "@/components/ui";
import { useAppSelector } from "@/stores/hooks";
import { useDepartments } from "@/hooks/useDepartments";
import { useRefreshProfile } from "@penn/web-common";
import { updateMyProfile } from "@penn/web-common";
import { profileFormSchema, type ProfileFormData } from "@/schemas/profile";
import { countryName, oidcConfig } from "@penn/web-common";

type AccountViewProps = {
  open: boolean;
  onClose: () => void;
};

export const AccountView = ({ open, onClose }: AccountViewProps) => {
  const user = useAppSelector((s) => s.auth.user);
  const departments = useDepartments();
  const refreshProfile = useRefreshProfile();
  // Present only when the central-directory overlay applied and held postal
  // data (see UsersService.overlaySharedProfile) — the address is then edited
  // on the platform profile, not here.
  const hasStructuredAddress = Boolean(
    user?.streetAddress || user?.city || user?.stateRegion || user?.postalCode || user?.country,
  );
  const issuer = oidcConfig()?.issuer;
  const issuerProfileUrl = issuer ? `${issuer}/profile` : null;

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const departmentOptions = useMemo(
    () =>
      (departments.data ?? []).map((d) => ({
        value: String(d.id),
        label: d.displayName,
      })),
    [departments.data],
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
      companyName: "",
      companyAddress: "",
      departmentId: "",
    },
  });

  useEffect(() => {
    if (!open || !user) return;
    reset({
      firstName: user.firstName ?? "",
      lastName: user.lastName ?? "",
      phone: user.phone ?? "",
      companyName: user.companyName ?? "",
      companyAddress: user.companyAddress ?? "",
      departmentId: user.departmentId ?? "",
    });
    setSubmitError(null);
    setSavedAt(null);
  }, [open, user, reset]);

  const onSubmit = async (data: ProfileFormData) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await updateMyProfile({
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        companyName: data.companyName,
        // The address is edited on the platform profile once it holds structured
        // postal data — never sent here in that case.
        ...(hasStructuredAddress ? {} : { companyAddress: data.companyAddress }),
        departmentId: Number(data.departmentId),
      });
      await refreshProfile();
      setSavedAt(Date.now());
    } catch (err: unknown) {
      const msg =
        err instanceof AxiosError
          ? ((err.response?.data as { error?: { message?: string } } | undefined)?.error
              ?.message ?? "Could not save your changes.")
          : "Could not save your changes.";
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <div className="flex items-center justify-end gap-2">
      <PennButton variant="secondary" size="md" onClick={onClose} disabled={submitting}>
        Close
      </PennButton>
      <PennButton
        variant="primary"
        size="md"
        onClick={handleSubmit(onSubmit)}
        disabled={submitting || !isDirty}
      >
        {submitting ? "Saving…" : "Update details"}
      </PennButton>
    </div>
  );

  return (
    <PennModal
      open={open}
      onClose={onClose}
      label="Account"
      title="Update account details"
      footer={footer}
    >
      <div className="p-6 space-y-5">
        {!user ? (
          <div className="py-8 text-center">
            <PennText as="p" size="sm" color="slate-500">
              No account information available.
            </PennText>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
            <Section label="Identity">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <PennField
                  label="First name"
                  required
                  htmlFor="profile-first-name"
                  error={errors.firstName?.message}
                >
                  <PennInput
                    id="profile-first-name"
                    placeholder="First name"
                    maxLength={30}
                    error={!!errors.firstName}
                    {...register("firstName")}
                  />
                </PennField>
                <PennField
                  label="Last name"
                  required
                  htmlFor="profile-last-name"
                  error={errors.lastName?.message}
                >
                  <PennInput
                    id="profile-last-name"
                    placeholder="Last name"
                    maxLength={30}
                    error={!!errors.lastName}
                    {...register("lastName")}
                  />
                </PennField>
                <PennField label="Email" htmlFor="profile-email" className="md:col-span-2">
                  <PennInput
                    id="profile-email"
                    value={user.email}
                    readOnly
                    className="bg-gray-100 cursor-not-allowed"
                  />
                </PennField>
              </div>
            </Section>

            <Section label="Contact">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <PennField
                  label="Telephone number"
                  required
                  htmlFor="profile-phone"
                  error={errors.phone?.message}
                >
                  <PennInput
                    id="profile-phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    error={!!errors.phone}
                    {...register("phone")}
                  />
                </PennField>
                <PennField
                  label="Company name"
                  required
                  htmlFor="profile-company"
                  error={errors.companyName?.message}
                >
                  <PennInput
                    id="profile-company"
                    placeholder="Company name"
                    error={!!errors.companyName}
                    {...register("companyName")}
                  />
                </PennField>
                {hasStructuredAddress ? (
                  <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <PennField label="Street address" htmlFor="profile-street-address">
                      <PennInput
                        id="profile-street-address"
                        value={user?.streetAddress ?? ""}
                        readOnly
                        className="bg-gray-100 cursor-not-allowed"
                      />
                    </PennField>
                    <PennField label="City" htmlFor="profile-city">
                      <PennInput
                        id="profile-city"
                        value={user?.city ?? ""}
                        readOnly
                        className="bg-gray-100 cursor-not-allowed"
                      />
                    </PennField>
                    <PennField label="State / Region" htmlFor="profile-state-region">
                      <PennInput
                        id="profile-state-region"
                        value={user?.stateRegion ?? ""}
                        readOnly
                        className="bg-gray-100 cursor-not-allowed"
                      />
                    </PennField>
                    <PennField label="Postal code" htmlFor="profile-postal-code">
                      <PennInput
                        id="profile-postal-code"
                        value={user?.postalCode ?? ""}
                        readOnly
                        className="bg-gray-100 cursor-not-allowed"
                      />
                    </PennField>
                    <PennField label="Country" htmlFor="profile-country">
                      <PennInput
                        id="profile-country"
                        value={user?.country ? countryName(user.country) : ""}
                        readOnly
                        className="bg-gray-100 cursor-not-allowed"
                      />
                    </PennField>
                    {issuerProfileUrl && (
                      <a
                        href={issuerProfileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="md:col-span-2 text-sm text-penn-600 hover:text-penn-700 underline"
                      >
                        Edit on your platform profile
                      </a>
                    )}
                  </div>
                ) : (
                  <PennField
                    label="Company address"
                    required
                    htmlFor="profile-company-address"
                    error={errors.companyAddress?.message}
                    className="md:col-span-2"
                  >
                    <PennTextarea
                      id="profile-company-address"
                      rows={3}
                      placeholder="Street, ZIP, City, Country"
                      error={!!errors.companyAddress}
                      {...register("companyAddress")}
                    />
                  </PennField>
                )}
              </div>
            </Section>

            <Section label="Organization">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <PennField
                  label="Function / Department"
                  required
                  htmlFor="profile-department"
                  error={errors.departmentId?.message}
                >
                  <Controller
                    name="departmentId"
                    control={control}
                    render={({ field }) => (
                      <PennDropdown
                        inputId="profile-department"
                        name={field.name}
                        options={departmentOptions}
                        value={field.value != null ? String(field.value) : ""}
                        onChange={(value) =>
                          field.onChange(
                            typeof value === "string" && value.length > 0
                              ? Number.parseInt(value, 10)
                              : "",
                          )
                        }
                        onBlur={field.onBlur}
                        placeholder={
                          departments.isLoading
                            ? "Loading departments…"
                            : departmentOptions.length === 0
                              ? "No departments available"
                              : "Select a function…"
                        }
                        isDisabled={departments.isLoading}
                        error={!!errors.departmentId}
                      />
                    )}
                  />
                </PennField>
                <PennField label="Region" htmlFor="profile-region">
                  <PennInput
                    id="profile-region"
                    value={user.region?.displayName ?? "—"}
                    readOnly
                    className="bg-gray-100 cursor-not-allowed"
                  />
                </PennField>
              </div>
            </Section>

            {submitError && (
              <div
                role="alert"
                className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {submitError}
              </div>
            )}

            {savedAt && !submitError && (
              <div
                role="status"
                className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-700"
              >
                Account details updated.
              </div>
            )}
          </form>
        )}
      </div>
    </PennModal>
  );
};

const Section = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div>
    <PennText
      as="p"
      size="xs"
      color="slate-500"
      font="semibold"
      className="uppercase mb-3"
    >
      {label}
    </PennText>
    {children}
  </div>
);
